/**
 * Organization invite links — universal helpers. An organization sends its
 * graduates `/organizations/<slug>/join` (optionally `?program=<slug>`) from
 * its own channels; DCM emails nobody. A graduate who joins through the link
 * gets a claim waiting for the organization, and the organization gets the
 * credit, with no reward.
 *
 * The `dcm_org_invite` cookie carries the invitation through sign-up. It is
 * separate from the referral cookie (`refCode`), which pays rewards. The
 * server validates its value every time it reads it.
 *
 * Aliases: invite graduates, organization invite, join link, dcm_org_invite
 * @module organization-invite
 */

import { z } from "zod";

export const ORG_INVITE_COOKIE_NAME = "dcm_org_invite";

/** 90 days, like the referral cookie. */
export const ORG_INVITE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90;

/** The organization and, for a program invite, its program. */
export interface OrganizationInviteRef {
  ccoId: string;
  ctpId: string | null;
}

/** A validated invitation, as the pages show it. */
export interface OrganizationInvite extends OrganizationInviteRef {
  organization: { name: string; slug: string; logoUrl: string | null };
  program: { name: string; slug: string } | null;
}

/** The credential the invite adds: the program, or the membership. */
export function inviteCredentialName(
  invite: Pick<OrganizationInvite, "organization" | "program">,
): string {
  return invite.program?.name ?? `Member of ${invite.organization.name}`;
}

const uuid = z.string().uuid();

export function formatOrganizationInviteCookie(
  ref: OrganizationInviteRef,
): string {
  return ref.ctpId ? `${ref.ccoId}:${ref.ctpId}` : ref.ccoId;
}

/** Parse a raw (possibly tampered) cookie value; null when malformed. */
export function parseOrganizationInviteCookie(
  value: string | null | undefined,
): OrganizationInviteRef | null {
  if (!value) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }
  const parts = decoded.split(":");
  if (parts.length > 2) return null;
  const [ccoId, ctpId] = parts;
  if (!uuid.safeParse(ccoId).success) return null;
  if (ctpId !== undefined && !uuid.safeParse(ctpId).success) return null;
  return { ccoId, ctpId: ctpId ?? null };
}

/**
 * Write the invite cookie from the landing page. Client-only, like the
 * referral cookie: SameSite=Lax, Secure on https.
 */
export function writeOrganizationInviteCookie(
  ref: OrganizationInviteRef,
): void {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; secure" : "";
  const value = encodeURIComponent(formatOrganizationInviteCookie(ref));
  document.cookie = `${ORG_INVITE_COOKIE_NAME}=${value}; path=/; max-age=${ORG_INVITE_MAX_AGE_SECONDS}; samesite=lax${secure}`;
}

/** The invite link an organization shares. */
export function buildOrganizationInviteUrl(params: {
  siteUrl: string;
  organizationSlug: string;
  programSlug: string | null;
}): string {
  const url = new URL(
    `/organizations/${encodeURIComponent(params.organizationSlug)}/join`,
    params.siteUrl,
  );
  if (params.programSlug) url.searchParams.set("program", params.programSlug);
  return url.toString();
}

/** `?add=` on Qualifications: `membership:<ccoId>` or `program:<ctpId>`. */
export function qualificationsAddParam(ref: OrganizationInviteRef): string {
  return ref.ctpId ? `program:${ref.ctpId}` : `membership:${ref.ccoId}`;
}

export interface CredentialRef {
  kind: "membership" | "program";
  /** `ccos.id` for a membership, `ctps.id` for a program. */
  targetId: string;
}

/** Parse `?add=`; null when malformed. */
export function parseCredentialParam(
  value: string | null | undefined,
): CredentialRef | null {
  const match = /^(membership|program):(.+)$/.exec(value ?? "");
  if (!match || !uuid.safeParse(match[2]).success) return null;
  return { kind: match[1] as CredentialRef["kind"], targetId: match[2] };
}

export const credentialRefSchema = z.object({
  kind: z.enum(["membership", "program"]),
  targetId: uuid,
}) satisfies z.ZodType<CredentialRef>;

/** The suggested message a representative sends with the link. */
export function buildInviteMessage(params: {
  organizationName: string;
  programName: string | null;
  link: string;
}): { subject: string; body: string } {
  const { organizationName, programName, link } = params;
  const credential = programName ?? `${organizationName} membership`;
  return {
    subject: `Show your ${credential} on Dream Coach Match`,
    body: [
      "Hi [first name],",
      `${organizationName} is on Dream Coach Match, where people look for a coach. Create your coach profile through the link below and your ${credential} is added for us to confirm, so clients see it was verified by ${organizationName}.`,
      link,
    ].join("\n\n"),
  };
}

/** A date input's `YYYY-MM-DD`. */
export const certificationDaySchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Pick a date")
  .refine((day) => {
    // `2026-02-31` matches the pattern but is not a day.
    const date = dayToDate(day);
    return !Number.isNaN(date.getTime()) && date.toISOString().startsWith(day);
  }, "Pick a date");

/** `YYYY-MM-DD` as UTC midnight, as program dates are stored. */
export function dayToDate(day: string): Date {
  return new Date(`${day}T00:00:00.000Z`);
}
